import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const COLUMN_LABELS = {
  gid: 'GID',
  act: 'Act',
  scene: 'Scene',
  is_stage_direction: 'Line Type',
  text: 'Text',
  speaker: 'Speaker',
  line_in_scene: 'Line in Scene'
};

const PAGE_SIZE = 25;

const UploadErrorReport = ({ errors }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [page, setPage] = useState(0);
  const [filterColumn, setFilterColumn] = useState('all');
  const [searchText, setSearchText] = useState('');

  if (!errors || errors?.length === 0) return null;

  // Derive unique columns present in errors
  const columnsInErrors = ['all', ...Array.from(new Set(errors.map(e => e.column)))];

  const filtered = errors?.filter(e => {
    const matchesColumn = filterColumn === 'all' || e?.column === filterColumn;
    const matchesSearch =
      !searchText ||
      e?.reason?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
      String(e?.csvLineNumber)?.includes(searchText) ||
      e?.column?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
      String(e?.value ?? '')?.toLowerCase()?.includes(searchText?.toLowerCase());
    return matchesColumn && matchesSearch;
  });

  const totalPages = Math.ceil(filtered?.length / PAGE_SIZE);
  const paginated = filtered?.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleFilterChange = (col) => {
    setFilterColumn(col);
    setPage(0);
  };

  const handleSearch = (e) => {
    setSearchText(e?.target?.value);
    setPage(0);
  };

  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(v => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-destructive/10 transition-colors duration-150"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-destructive/15 flex items-center justify-center flex-shrink-0">
            <Icon name="AlertTriangle" size={16} className="text-destructive" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-destructive">
              Validation Report — {errors?.length} issue{errors?.length !== 1 ? 's' : ''} found
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Review the rows below and correct your CSV before re-uploading
            </p>
          </div>
        </div>
        <Icon
          name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
          size={18}
          className="text-muted-foreground flex-shrink-0"
        />
      </button>
      {isExpanded && (
        <div className="border-t border-destructive/20">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3 bg-background/50">
            {/* Column filter pills */}
            <div className="flex flex-wrap gap-1.5">
              {columnsInErrors?.map(col => (
                <button
                  key={col}
                  onClick={() => handleFilterChange(col)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors duration-150 ${
                    filterColumn === col
                      ? 'bg-destructive text-destructive-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {col === 'all' ? 'All columns' : (COLUMN_LABELS?.[col] ?? col)}
                  {col !== 'all' && (
                    <span className="ml-1 opacity-70">
                      ({errors?.filter(e => e?.column === col)?.length})
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative sm:ml-auto">
              <Icon
                name="Search"
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
              />
              <input
                type="text"
                placeholder="Search errors..."
                value={searchText}
                onChange={handleSearch}
                className="pl-8 pr-3 py-1.5 text-xs rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring w-48"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50 border-y border-border/50">
                  <th className="px-4 py-2.5 text-left font-semibold text-muted-foreground w-20">Line #</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-muted-foreground w-28">Column</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-muted-foreground w-32">Value</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-muted-foreground">Reason for Rejection</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {paginated?.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                      No errors match your filter
                    </td>
                  </tr>
                ) : (
                  paginated?.map((err, i) => (
                    <tr
                      key={`${err?.csvLineNumber}-${err?.column}-${i}`}
                      className="hover:bg-destructive/5 transition-colors duration-100"
                    >
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1 font-mono font-semibold text-destructive">
                          <Icon name="Hash" size={11} className="opacity-60" />
                          {err?.csvLineNumber}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-block px-2 py-0.5 rounded bg-muted font-mono text-foreground">
                          {COLUMN_LABELS?.[err?.column] ?? err?.column}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        {err?.value !== '' && err?.value != null ? (
                          <span className="font-mono text-muted-foreground truncate max-w-[120px] block" title={String(err?.value)}>
                            "{String(err?.value)?.length > 20 ? String(err?.value)?.slice(0, 20) + '…' : String(err?.value)}"
                          </span>
                        ) : (
                          <span className="italic text-muted-foreground/60">(empty)</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-foreground/80">{err?.reason}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-border/30 bg-background/50">
              <span className="text-xs text-muted-foreground">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered?.length)} of {filtered?.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-1.5 rounded hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Icon name="ChevronLeft" size={14} className="text-muted-foreground" />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-7 h-7 rounded text-xs font-medium transition-colors ${
                        pageNum === page
                          ? 'bg-destructive text-destructive-foreground'
                          : 'hover:bg-muted text-muted-foreground'
                      }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page === totalPages - 1}
                  className="p-1.5 rounded hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadErrorReport;
